/**
 * Customer Auth Page - Todo en Local Marketplace
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Mail, Lock, User, Phone, ArrowLeft, Eye, EyeOff, AlertCircle, ShoppingBag, Heart, Truck, Shield, Check, X as XIcon, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import api, { sendCustomerVerificationCode, verifyCustomerEmail, customerForgotPassword, customerResetPassword } from '../../services/api';
import { getErrorMessage } from '../../utils/errorUtils';

// Brand colors - Professional & Modern
const COLORS = {
  navyBlue: '#1e3a5f',      // Azul marino profundo
  darkSlate: '#0f172a',     // Slate muy oscuro
  orange: '#F37322',        // Naranja - CTA principal
  lightGray: '#f8fafc',
  white: '#ffffff'
};

const CustomerAuth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Validate redirect to prevent open redirect — only allow relative paths
  const rawRedirect = searchParams.get('redirect') || '/';
  const redirectTo = (rawRedirect.startsWith('/') && !rawRedirect.startsWith('//')) ? rawRedirect : '/';

  const [mode, setMode] = useState(location.hash === '#register' ? 'register' : 'login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationMode, setVerificationMode] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const codeInputRefs = useRef([]);

  // Recovery state
  const [recoveryStep, setRecoveryStep] = useState(null); // null | 'email' | 'code' | 'password'
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [recoveryResendCountdown, setRecoveryResendCountdown] = useState(0);
  const recoveryCodeRefs = useRef([]);

  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: ''
  });

  // Countdown timer for resend
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => setResendCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  useEffect(() => {
    if (recoveryResendCountdown <= 0) return;
    const timer = setInterval(() => setRecoveryResendCountdown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [recoveryResendCountdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'register') {
      if (form.password.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (!/[A-Z]/.test(form.password)) {
        toast.error('La contraseña debe tener al menos una letra mayúscula');
        return;
      }
    }

    try {
      setLoading(true);
      setError('');

      if (mode === 'login') {
        const response = await api.post('/marketplace/customers/login', {
          email: form.email,
          password: form.password
        });

        localStorage.setItem('customer_token', response.data.token);
        localStorage.setItem('customer_data', JSON.stringify(response.data.customer));

        if (!response.data.customer.email_verified) {
          setVerificationEmail(form.email);
          setVerificationMode(true);
          toast.info('Verifica tu correo para continuar');
        } else {
          toast.success('¡Bienvenido!');
          navigate(redirectTo);
        }
      } else {
        await api.post('/marketplace/customers/register', form);

        // Auto login after register
        const loginResponse = await api.post('/marketplace/customers/login', {
          email: form.email,
          password: form.password
        });

        localStorage.setItem('customer_token', loginResponse.data.token);
        localStorage.setItem('customer_data', JSON.stringify(loginResponse.data.customer));

        setVerificationEmail(form.email);
        setVerificationMode(true);
        toast.info('Revisa tu correo para el código de verificación');
      }
    } catch (err) {
      const msg = getErrorMessage(err, mode === 'login'
        ? 'Correo o contraseña incorrectos'
        : 'No se pudo crear la cuenta');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // ---- Verification handlers ----
  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...verificationCode];
      digits.forEach((d, i) => { if (index + i < 6) newCode[index + i] = d; });
      setVerificationCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      codeInputRefs.current[nextIndex]?.focus();
      if (newCode.every(d => d !== '')) submitVerification(newCode.join(''));
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newCode = [...verificationCode];
    newCode[index] = digit;
    setVerificationCode(newCode);
    if (digit && index < 5) codeInputRefs.current[index + 1]?.focus();
    if (digit && newCode.every(d => d !== '')) submitVerification(newCode.join(''));
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...verificationCode];
      pasted.split('').forEach((d, i) => { newCode[i] = d; });
      setVerificationCode(newCode);
      codeInputRefs.current[Math.min(pasted.length, 5)]?.focus();
      if (newCode.every(d => d !== '')) submitVerification(newCode.join(''));
    }
  };

  const submitVerification = async (codeStr) => {
    if (verifying) return;
    const finalCode = codeStr || verificationCode.join('');
    if (finalCode.length !== 6) return;
    try {
      setVerifying(true);
      const res = await verifyCustomerEmail(verificationEmail, finalCode);
      // Update localStorage with verified customer
      if (res.data.customer) {
        localStorage.setItem('customer_data', JSON.stringify(res.data.customer));
      } else {
        const existing = JSON.parse(localStorage.getItem('customer_data') || '{}');
        existing.email_verified = true;
        localStorage.setItem('customer_data', JSON.stringify(existing));
      }
      toast.success('Correo verificado');
      navigate(redirectTo);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Código incorrecto'));
      setVerificationCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCountdown > 0) return;
    try {
      await sendCustomerVerificationCode(verificationEmail);
      setResendCountdown(60);
      toast.success('Código reenviado');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al reenviar'));
    }
  };

  // --- Recovery handlers ---
  const handleSendResetCode = async (e) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) return;
    setLoading(true);
    try {
      await customerForgotPassword(recoveryEmail.trim());
      toast.success('Si el correo está registrado, recibirás un código');
      setRecoveryStep('code');
      setRecoveryResendCountdown(60);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al enviar código'));
    } finally {
      setLoading(false);
    }
  };

  const handleRecoveryResendCode = async () => {
    if (recoveryResendCountdown > 0) return;
    try {
      await customerForgotPassword(recoveryEmail.trim());
      setRecoveryResendCountdown(60);
      toast.success('Código reenviado');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al reenviar'));
    }
  };

  const handleRecoveryCodeChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...recoveryCode];
      digits.forEach((d, i) => { if (index + i < 6) newCode[index + i] = d; });
      setRecoveryCode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      recoveryCodeRefs.current[nextIndex]?.focus();
      if (newCode.every(d => d !== '')) setRecoveryStep('password');
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newCode = [...recoveryCode];
    newCode[index] = digit;
    setRecoveryCode(newCode);
    if (digit && index < 5) recoveryCodeRefs.current[index + 1]?.focus();
    if (digit && newCode.every(d => d !== '')) setRecoveryStep('password');
  };

  const handleRecoveryCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !recoveryCode[index] && index > 0) {
      recoveryCodeRefs.current[index - 1]?.focus();
    }
  };

  const handleRecoveryCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newCode = [...recoveryCode];
      pasted.split('').forEach((d, i) => { newCode[i] = d; });
      setRecoveryCode(newCode);
      recoveryCodeRefs.current[Math.min(pasted.length, 5)]?.focus();
      if (newCode.every(d => d !== '')) setRecoveryStep('password');
    }
  };

  const handleRecoveryVerifyAndContinue = () => {
    const codeStr = recoveryCode.join('');
    if (codeStr.length !== 6) {
      toast.error('Ingresa el código completo de 6 dígitos');
      return;
    }
    setRecoveryStep('password');
  };

  const handleCustomerResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('La contraseña debe tener al menos una letra mayúscula');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      const codeStr = recoveryCode.join('');
      await customerResetPassword(recoveryEmail.trim(), codeStr, newPassword);
      toast.success('Contraseña actualizada exitosamente');
      resetRecovery();
    } catch (err) {
      const msg = getErrorMessage(err, 'Error al cambiar contraseña');
      toast.error(msg);
      if (msg.includes('expirado') || msg.includes('incorrecto')) {
        setRecoveryStep('code');
        setRecoveryCode(['', '', '', '', '', '']);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetRecovery = () => {
    setRecoveryStep(null);
    setRecoveryEmail('');
    setRecoveryCode(['', '', '', '', '', '']);
    setNewPassword('');
    setConfirmPassword('');
    setLoading(false);
  };

  const maskedVerifEmail = verificationEmail
    ? verificationEmail.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => a + '*'.repeat(Math.min(b.length, 6)) + c)
    : '';

  const benefits = [
    { icon: ShoppingBag, title: 'Compra local', desc: 'Productos de negocios cerca de ti' },
    { icon: Heart, title: 'Favoritos', desc: 'Guarda tus productos preferidos' },
    { icon: Truck, title: 'Entrega a domicilio', desc: 'Recibe en la puerta de tu casa' },
    { icon: Shield, title: 'Compra segura', desc: 'Tus datos siempre protegidos' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Main */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 py-8">
        <div className="w-full max-w-4xl mb-3">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al marketplace
          </Link>
        </div>
        <div className="w-full max-w-4xl flex rounded-2xl overflow-hidden shadow-sm bg-white">
          {/* Left panel - benefits (desktop only) */}
          <div className="hidden lg:flex flex-col justify-center w-[380px] p-10" style={{ backgroundColor: COLORS.navyBlue }}>
            <h2 className="text-2xl font-bold text-white mb-2">
              Todo En Local
            </h2>
            <p className="text-blue-200 text-sm mb-8">
              Tu marketplace local favorito
            </p>
            <div className="space-y-5">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">{b.title}</h3>
                    <p className="text-blue-200 text-xs mt-0.5">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div className="flex-1 p-6 sm:p-8">
            {recoveryStep ? (
              /* ---- Password Recovery UI ---- */
              recoveryStep === 'email' ? (
                <div className="max-w-sm mx-auto">
                  <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
                      <KeyRound className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
                    </div>
                    <h1 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>Recuperar contraseña</h1>
                    <p className="mt-2 text-sm text-slate-500 text-center">Ingresa tu correo y te enviaremos un código de verificación</p>
                  </div>
                  <form onSubmit={handleSendResetCode} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Correo electrónico</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          required
                          value={recoveryEmail}
                          onChange={(e) => setRecoveryEmail(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                          style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                          placeholder="tu@email.com"
                          autoFocus
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                      style={{ backgroundColor: COLORS.orange }}
                    >
                      {loading ? 'Enviando...' : 'Enviar código'}
                    </button>
                  </form>
                  <button onClick={resetRecovery} className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
                    Volver al login
                  </button>
                </div>
              ) : recoveryStep === 'code' ? (
                <div className="max-w-sm mx-auto">
                  <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
                      <Mail className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
                    </div>
                    <h1 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>Ingresa el código</h1>
                    <p className="mt-2 text-sm text-slate-500 text-center">Enviamos un código de 6 dígitos a</p>
                    <p className="mt-1 text-sm font-medium" style={{ color: COLORS.navyBlue }}>{recoveryEmail}</p>
                  </div>

                  <div className="flex justify-center gap-2 mb-6" onPaste={handleRecoveryCodePaste}>
                    {recoveryCode.map((digit, index) => (
                      <input
                        key={index}
                        ref={el => recoveryCodeRefs.current[index] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={digit}
                        onChange={(e) => handleRecoveryCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleRecoveryCodeKeyDown(index, e)}
                        className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:outline-none focus:border-transparent focus:ring-2 transition-all"
                        style={{ '--tw-ring-color': COLORS.navyBlue + '60', color: COLORS.darkSlate }}
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>

                  <button
                    onClick={handleRecoveryVerifyAndContinue}
                    disabled={recoveryCode.some(d => d === '')}
                    className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                    style={{ backgroundColor: COLORS.orange }}
                  >
                    Continuar
                  </button>

                  <div className="text-center mt-5">
                    <p className="text-sm text-slate-500 mb-2">¿No recibiste el código?</p>
                    <button
                      onClick={handleRecoveryResendCode}
                      disabled={recoveryResendCountdown > 0}
                      className="text-sm font-semibold disabled:opacity-50 transition-colors"
                      style={{ color: recoveryResendCountdown > 0 ? '#94a3b8' : COLORS.orange }}
                    >
                      {recoveryResendCountdown > 0 ? `Reenviar en ${recoveryResendCountdown}s` : 'Reenviar código'}
                    </button>
                  </div>
                  <button onClick={resetRecovery} className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
                    Volver al login
                  </button>
                </div>
              ) : (
                /* recoveryStep === 'password' */
                <div className="max-w-sm mx-auto">
                  <div className="flex flex-col items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
                      <Lock className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
                    </div>
                    <h1 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>Nueva contraseña</h1>
                    <p className="mt-2 text-sm text-slate-500 text-center">Ingresa tu nueva contraseña</p>
                  </div>
                  <form onSubmit={handleCustomerResetPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Nueva contraseña</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          required
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                          style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                          placeholder="Mínimo 6 caracteres"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                          {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                      {newPassword.length > 0 && (
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                          <span className={`flex items-center gap-1 text-xs ${newPassword.length >= 6 ? 'text-green-600' : 'text-slate-400'}`}>
                            {newPassword.length >= 6 ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                            6+ caracteres
                          </span>
                          <span className={`flex items-center gap-1 text-xs ${/[A-Z]/.test(newPassword) ? 'text-green-600' : 'text-slate-400'}`}>
                            {/[A-Z]/.test(newPassword) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                            1 mayúscula
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Confirmar contraseña</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          required
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                          style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                          placeholder="Repite tu contraseña"
                        />
                      </div>
                      {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                        <p className="text-xs text-red-500 mt-1">Las contraseñas no coinciden</p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={loading || newPassword.length < 6 || !/[A-Z]/.test(newPassword) || newPassword !== confirmPassword}
                      className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                      style={{ backgroundColor: COLORS.orange }}
                    >
                      {loading ? 'Cambiando...' : 'Cambiar contraseña'}
                    </button>
                  </form>
                  <button onClick={resetRecovery} className="w-full mt-4 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors">
                    Volver al login
                  </button>
                </div>
              )
            ) : verificationMode ? (
              /* ---- Verification Code UI ---- */
              <div className="max-w-sm mx-auto">
                <div className="flex flex-col items-center mb-8">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: COLORS.navyBlue + '10' }}>
                    <ShieldCheck className="w-8 h-8" style={{ color: COLORS.navyBlue }} />
                  </div>
                  <h1 className="text-2xl font-bold text-center" style={{ color: COLORS.darkSlate }}>
                    Verifica tu correo
                  </h1>
                  <p className="mt-2 text-sm text-slate-500 text-center">
                    Enviamos un codigo de 6 digitos a
                  </p>
                  <p className="flex items-center gap-1.5 mt-1 text-sm font-medium" style={{ color: COLORS.navyBlue }}>
                    <Mail className="w-4 h-4" />
                    {maskedVerifEmail}
                  </p>
                </div>

                <div className="flex justify-center gap-2 mb-6" onPaste={handleCodePaste}>
                  {verificationCode.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => codeInputRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(index, e)}
                      className="w-12 h-14 text-center text-xl font-bold border-2 border-slate-200 rounded-xl focus:outline-none focus:border-transparent focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': COLORS.navyBlue + '60', color: COLORS.darkSlate }}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                <button
                  onClick={() => submitVerification()}
                  disabled={verifying || verificationCode.some(d => d === '')}
                  className="w-full py-3 text-sm font-semibold rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  {verifying ? 'Verificando...' : 'Verificar Correo'}
                </button>

                <div className="text-center mt-5">
                  <p className="text-sm text-slate-500 mb-2">No recibiste el codigo?</p>
                  <button
                    onClick={handleResendCode}
                    disabled={resendCountdown > 0}
                    className="text-sm font-semibold disabled:opacity-50 transition-colors"
                    style={{ color: resendCountdown > 0 ? '#94a3b8' : COLORS.orange }}
                  >
                    {resendCountdown > 0 ? `Reenviar en ${resendCountdown}s` : 'Reenviar codigo'}
                  </button>
                </div>
              </div>
            ) : (
              /* ---- Login / Register Form ---- */
              <>
                {/* Logo + Title */}
                <div className="text-center mb-6">
                  <img src="/logo.png" alt="Todo en Local" className="h-12 w-auto mx-auto mb-3" />
                  <h1 className="text-2xl font-bold" style={{ color: COLORS.darkSlate }}>
                    {mode === 'login' ? 'Bienvenido de vuelta' : 'Crea tu cuenta'}
                  </h1>
                  <p className="text-sm text-slate-500 mt-1">
                    {mode === 'login' ? 'Ingresa a tu cuenta de cliente' : 'Regístrate para comenzar a comprar'}
                  </p>
                </div>

                {/* Tabs */}
                <div className="flex mb-6 bg-slate-100 rounded-lg p-1">
                  <button
                    onClick={() => { setMode('login'); setError(''); }}
                    className={`flex-1 py-2.5 rounded-md text-sm font-semibold transition-all ${
                      mode === 'login' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-600'
                    }`}
                  >
                    Iniciar Sesión
                  </button>
                  <button
                    onClick={() => { setMode('register'); setError(''); }}
                    className={`flex-1 py-2.5 rounded-md text-sm font-semibold transition-all ${
                      mode === 'register' ? 'bg-white shadow text-slate-800' : 'text-slate-500 hover:text-slate-600'
                    }`}
                  >
                    Crear Cuenta
                  </button>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {mode === 'register' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Nombre completo</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="text"
                            required
                            value={form.full_name}
                            onChange={(e) => setForm({...form, full_name: e.target.value})}
                            placeholder="Tu nombre"
                            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                            style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                          <input
                            type="tel"
                            value={form.phone}
                            onChange={(e) => setForm({...form, phone: e.target.value})}
                            placeholder="10 dígitos"
                            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                            style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Correo electrónico</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({...form, email: e.target.value})}
                        placeholder="tu@email.com"
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                        style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={form.password}
                        onChange={(e) => setForm({...form, password: e.target.value})}
                        placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : '••••••'}
                        className="w-full pl-10 pr-10 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent transition-shadow"
                        style={{ '--tw-ring-color': COLORS.navyBlue + '40' }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {mode === 'register' && form.password.length > 0 && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        <span className={`flex items-center gap-1 text-xs ${form.password.length >= 6 ? 'text-green-600' : 'text-slate-400'}`}>
                          {form.password.length >= 6 ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                          6+ caracteres
                        </span>
                        <span className={`flex items-center gap-1 text-xs ${/[A-Z]/.test(form.password) ? 'text-green-600' : 'text-slate-400'}`}>
                          {/[A-Z]/.test(form.password) ? <Check className="w-3 h-3" /> : <XIcon className="w-3 h-3" />}
                          1 mayúscula
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-lg text-white font-semibold disabled:opacity-50 transition-all hover:opacity-90"
                    style={{ backgroundColor: COLORS.orange }}
                  >
                    {loading ? 'Procesando...' : mode === 'login' ? 'Iniciar Sesión' : 'Crear Cuenta'}
                  </button>
                </form>

                {mode === 'login' && (
                  <>
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        onClick={() => setRecoveryStep('email')}
                        className="text-sm font-medium hover:underline transition-colors"
                        style={{ color: COLORS.navyBlue }}
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                    <p className="text-center text-sm text-slate-500 mt-3">
                      ¿No tienes cuenta?{' '}
                      <button onClick={() => setMode('register')} className="font-semibold" style={{ color: COLORS.orange }}>
                        Regístrate
                      </button>
                    </p>
                  </>
                )}

                {mode === 'register' && (
                  <p className="text-center text-sm text-slate-500 mt-4">
                    Al registrarte, aceptas nuestros{' '}
                    <Link to="/terminos" style={{ color: COLORS.orange }}>términos</Link> y{' '}
                    <Link to="/privacidad" style={{ color: COLORS.orange }}>política de privacidad</Link>
                  </p>
                )}

                {/* Business login link */}
                <div className="mt-6 pt-5 border-t border-slate-100 text-center">
                  <p className="text-sm text-slate-500">
                    ¿Eres dueño de un negocio?{' '}
                    <Link to="/login" className="font-semibold" style={{ color: COLORS.navyBlue }}>
                      Ingresar al Dashboard
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerAuth;
